import { zodResolver } from '@hookform/resolvers/zod';
import { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  FileMimeTypes,
  FilePicker,
  ItemStatus,
  S3FileUploader,
} from '@/modules/common';

import { maxOverlayPreviewSize } from '../constants';
import { OverlayUploadUrlData } from '../types';
import { OverlayFormSchema } from '../validations';

type OverlayFormValues = z.infer<typeof OverlayFormSchema>;

interface MaskFormProps {
  editMode?: boolean;
  uploadUrlData?: OverlayUploadUrlData | null;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: OverlayFormValues) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
  defaultValues?: OverlayFormValues & { imagePublicUrl: string };
}

const defaultFormValues = {
  name: '',
  status: '',
  imageUploadUrl: '',
  updatedAt: undefined,
};

export function OverlayForm({
  onSubmit,
  children,
  editMode,
  defaultValues,
  uploadUrlData,
  refetchUploadCredentials,
}: MaskFormProps) {
  const form = useForm<OverlayFormValues>({
    resolver: zodResolver(OverlayFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async () => {
    await onSubmit(form.getValues());
  };

  const filesConfig = [
    {
      key: 'imageUploadUrl',
      uploadUrl: uploadUrlData?.imageUploadUrl,
      fileUrl: defaultValues?.imagePublicUrl,
    },
  ];

  return (
    <Form {...form}>
      <S3FileUploader
        config={filesConfig}
        onComplete={handleFormSubmit}
        refetchCredentials={refetchUploadCredentials}
      >
        {({
          retryUpload,
          filesState,
          isUploading,
          onFileChange,
          uploadFiles,
        }) => (
          <form onSubmit={form.handleSubmit(uploadFiles)}>
            {children({ isLoading: isUploading })}

            <div className="flex flex-col gap-10 max-w-6xl">
              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Overlay name</FormLabel>
                      <FormControl>
                        <Input placeholder="Add mask name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="updatedAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last modified</FormLabel>
                      <FormControl>
                        <Input
                          disabled
                          placeholder="YYYY-MM-DD"
                          required={false}
                          readOnly
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ItemStatus
                  name="status"
                  isDisabled={!editMode}
                  control={form.control}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <div className="col-span-2 flex flex-col gap-6">
                  <h4 className="text-2xl font-semibold">Upload Overlay</h4>

                  <FilePicker
                    control={form.control}
                    label="Overlay"
                    name="imageUploadUrl"
                    withFilePreview
                    accept={[FileMimeTypes.Png]}
                    onRetry={retryUpload}
                    isUploading={isUploading}
                    file={filesState.imageUploadUrl?.file}
                    error={filesState.imageUploadUrl?.error}
                    onFileChange={onFileChange}
                    placeholder="Upload Overlay"
                    tips={
                      <p className="text-sm text-zinc-500">
                        The file must be in
                        <span className="text-zinc-100">&nbsp;.png&nbsp;</span>
                        format and maximum size 10 MB.
                      </p>
                    }
                    maxFileSize={maxOverlayPreviewSize}
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </S3FileUploader>
    </Form>
  );
}
